<template>
	<div class="container_wrapper">
		<Swiper class="container" v-bind="mainSwiperOptions" @swiper="onSwiper">
			<SwiperSlide
				v-for="(item, index) in props.data"
				:key="`${index}`"
				class="youtube_item"
				@click="onClickYoutube(item.youtube_url)"
			>
				<div class="container_item_img">
					<img class="item_img" :src="item.youtube_thumbnail_url" alt="대가 일러스트" loading="lazy" />
					<div class="img_button">
						<img src="@/assets/img/common/youtube.png" alt="유튜브 재생 아이콘" loading="lazy" />
					</div>
				</div>
			</SwiperSlide>
			<div class="custom-navigation">
				<button class="custom-nav-prev" aria-label="prev-arrow"><IconArrowPrev width="22" height="22" /></button>
				<div class="swiper-pagination"></div>
				<button class="custom-nav-next" aria-label="next-arrow"><IconArrowNext width="22" height="22" /></button>
			</div>
		</Swiper>
	</div>
</template>
<script setup lang="ts">
// core
import { onMounted, onUnmounted, ref } from 'vue';
// lib
import { debounce } from 'lodash';
import { FreeMode, Navigation, Pagination } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
//swiper
import { Swiper, SwiperSlide } from 'swiper/vue';

import { youtube } from '@/api/home';

import IconArrowNext from '../atom/icon/IconArrowNext.vue';
import IconArrowPrev from '../atom/icon/IconArrowPrev.vue';

// -----variable-----
const props = defineProps<{
	data: youtube[];
	selectedPk?: string;
}>();
const swiperComponent = ref();
const previousWidth = ref(window.innerWidth);

// -----swiper-----
const mainSwiperOptions: SwiperOptions = {
	modules: [FreeMode, Pagination, Navigation],
	slidesPerView: 'auto',
	spaceBetween: 10,
	slidesOffsetBefore: 20,
	slidesOffsetAfter: 20,
	freeMode: false,
	observer: true,
	observeParents: true,
	updateOnWindowResize: true,
	pagination: {
		el: '.swiper-pagination',
		type: 'fraction',
		formatFractionCurrent: function (number: number) {
			return ('0' + number).slice(-2);
		},
		formatFractionTotal: function (number: number) {
			return ('0' + number).slice(-2);
		},
		renderFraction: (currentClass: string, totalClass: string) => {
			return (
				'<span class="' +
				currentClass +
				'"></span>' +
				'<span class="fraction-divider">|</span>' +
				'<span class="' +
				totalClass +
				'"></span>'
			);
		},
	},
	navigation: {
		nextEl: '.custom-nav-next',
		prevEl: '.custom-nav-prev',
	},
	loop: true,
};

// -----function-----
// 해당 마스터로 슬라이드 이동
const onSwiper = (swiper: SwiperOptions) => {
	swiperComponent.value = swiper;
};

const onClickYoutube = (url: string) => {
	console.log(url);
	// let agent = navigator.userAgent.toLowerCase();
	// if (agent.indexOf('android') !== -1 && window.masterpick) {
	// 	openNativeBrowser(url);
	// } else if (
	// 	agent.indexOf('iphone') !== -1 ||
	// 	agent.indexOf('ipad') !== -1 ||
	// 	(agent.indexOf('macintosh') !== -1 &&
	// 		window.webkit &&
	// 		window.webkit.messageHandlers &&
	// 		window.webkit.messageHandlers.nativeUrlOpenBrowser)
	// ) {
	// 	openNativeBrowser(url);
	// } else {
	window.open(url, '_blank');
	// }
};

// 화면 크기 변경 시 리로드 함수 (swiper 작동 오류)
const handleResize = debounce(() => {
	const currentWidth = window.innerWidth;
	if (currentWidth !== previousWidth.value) {
		previousWidth.value = currentWidth;

		// Swiper 인스턴스 업데이트
		if (swiperComponent.value) {
			swiperComponent.value.update();
			swiperComponent.value.updateSlides();
			swiperComponent.value.updateSize();
			swiperComponent.value.updateProgress();
		}
	}
}, 300); // debounce 시간을 1000ms에서 300ms로 줄임

// -----composition-----
onMounted(() => {
	window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
	window.removeEventListener('resize', handleResize);
	handleResize.cancel();
});
</script>

<style lang="scss" scoped>
.container {
	.container_item_img {
		position: relative;
		width: 22rem;
		height: 12rem;
		.item_img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			object-position: center;
			border-radius: 1rem;
		}
		.img_button {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			img {
				width: 4rem;
			}
		}
	}
	.custom-navigation {
		position: absolute;
		bottom: 0;
		right: 0;
		display: flex;
		align-items: center;
		gap: 1rem;
		height: 2.6rem;
		border-radius: 10rem;
		z-index: 10;
		padding: 0.2rem 0.4rem;
		.custom-nav-prev {
			justify-content: flex-end;
		}
		.custom-nav-prev,
		.custom-nav-next {
			height: 100%;
			background: transparent;
			border: none;
			cursor: pointer;
			color: var(--color-base-white);
			display: flex;
			align-items: center;
			&::after {
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
			}
		}

		:deep(.swiper-pagination) {
			position: static;
			width: auto;
			display: flex;
			align-items: center;
			gap: 0.5rem;
			font-size: var(--font-s-small);
			font-weight: var(--font-w-light);
			width: 100%;
			font-weight: var(--font-w-regular);
			font-size: var(--font-s-small);
			color: #fff;
			bottom: 0;
			text-align: end;

			.swiper-pagination-current {
				color: var(--color-base-white);
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.56rem;
			}

			.fraction-divider {
				color: var(--color-base-mid);
				margin: 0 0.5rem;
			}

			.swiper-pagination-total {
				color: var(--color-base-light);
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.56rem;
				.swiper-button-prev {
					position: unset;
					margin-top: 0;
					height: inherit;
					width: 2.2rem;
				}
				.swiper-button-next {
					position: unset;
					margin-top: 0;
					height: inherit;
					width: 2.2rem;
				}
				.swiper-button-prev::after {
					color: var(--color-base-white);
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: 1.551rem;
				}
				.swiper-button-next::after {
					color: var(--color-base-white);
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: 1.551rem;
				}
			}
		}
	}
}
.swiper-slide {
	width: unset;
	height: 16.2rem;
}
</style>
